import axios from 'axios';
import { trackEvent } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

const resolveSiteMediaUrl = (url) => {
  if (!url) return '';
  return url.startsWith('/api/') ? `${BACKEND_URL}${url}` : url;
};

export const fetchHomepageSettings = async () => {
  try {
    const res = await api.get('/site-settings/homepage');
    return {
      ...res.data,
      hero_video_url: resolveSiteMediaUrl(res.data.hero_video_url),
      hero_poster_url: resolveSiteMediaUrl(res.data.hero_poster_url),
    };
  } catch {
    return null;
  }
};

// Cars
export const fetchCars = async (params = {}) => {
  try {
    const res = await api.get('/cars', { params });
    return res.data.map(normalizeCar);
  } catch {
    return null; // fallback to mock
  }
};

export const fetchCar = async (id) => {
  try {
    const res = await api.get(`/cars/${id}`);
    return normalizeCar(res.data);
  } catch {
    return null;
  }
};

export const fetchCarFilters = async () => {
  try {
    const res = await api.get('/cars/filters');
    return res.data;
  } catch {
    return null;
  }
};

export const trackCarView = async (id) => {
  try {
    await api.post(`/cars/${id}/view`);
  } catch {
    // silent fail
  }
};

// Normalize car data from API to match frontend format
const normalizeCar = (car) => ({
  id: car.id,
  slug: car.slug || '',
  brand: car.brand,
  model: car.model,
  version: car.version,
  date: car.date,
  year: car.year,
  mileage: car.mileage,
  mileageDisplay: car.mileage_display || `${(car.mileage || 0).toLocaleString('pt-PT')} km`,
  horsepower: car.horsepower,
  fuel: car.fuel,
  transmission: car.transmission,
  traction: car.traction || '',
  displacement: car.displacement || '',
  gears: car.gears || '',
  seats: car.seats || '',
  car_class: car.car_class || '',
  condition: car.condition || '',
  segment: car.segment || '',
  origin: car.origin || '',
  registrations: car.registrations || '',
  full_service_history: car.full_service_history || false,
  non_smoker: car.non_smoker || false,
  second_key: car.second_key || false,
  classic: car.classic || false,
  warranty: car.warranty || '',
  inspection_until: car.inspection_until || '',
  factory_warranty_until: car.factory_warranty_until || '',
  factory_warranty_km: car.factory_warranty_km || '',
  chassis: car.chassis || '',
  price: car.price,
  priceDisplay: car.price_display || `${(car.price || 0).toLocaleString('pt-PT')} €`,
  financingUntil: car.financing_until || '',
  badge: car.badge,
  badgeColor: car.badge_color || '#C41E3A',
  category: car.category,
  color: car.color,
  doors: car.doors,
  description: car.description,
  features: car.features || [],
  equipment: car.equipment || {},
  featured: car.featured,
  active: car.active,
  videoUrl: car.video_url || '',
  availability: car.availability || 'available',
  views: car.views || 0,
  images: (car.images || []).map(img => ({
    ...img,
    url: img.url?.startsWith('http') ? img.url : `${BACKEND_URL}${img.url}`,
    thumbnail_url: img.thumbnail_url
      ? (img.thumbnail_url.startsWith('http') ? img.thumbnail_url : `${BACKEND_URL}${img.thumbnail_url}`)
      : ''
  })),
});

// Chat IA
export const sendChatMessage = async (sessionId, messages) => {
  const res = await api.post('/chat', { session_id: sessionId, messages });
  return res.data; // { message, cars, lead_saved }
};

// Leads / Form submissions
export const submitLead = async (type, formData) => {
  const fd = new FormData();
  Object.entries(formData).forEach(([k, v]) => {
    if (v !== undefined && v !== null) fd.append(k, v);
  });
  const res = await api.post(`/leads/${type}`, fd);
  trackEvent('lead_submitted', {
    lead_type: type,
    car_id: formData.car_id || formData.carId || undefined,
  });
  return res.data;
};
